/**
 * Milk customers (BR-23, BR-24).
 *
 * The endpoints have existed since P3 and the client only ever read the list,
 * so a new customer could not be added from the app at all — and the delivery
 * round is drawn from this list, which meant the round could not grow either.
 *
 * The one distinction that matters here is regular versus walk-in, because it
 * decides how their milk turns into money:
 *
 *   A regular customer is on the round. Their litres are recorded daily and
 *   billed once a month by invoice, at the rate agreed here. That is why the
 *   rate is required for them and why they are refused at the gate — selling
 *   them milk for cash as well would bill the same litres twice.
 *
 *   A walk-in buys at the gate for cash. No rate, no bill, no round.
 *
 * Getting that wrong is not a cosmetic mistake, so the screen says which is
 * which rather than leaving it to a word in a dropdown.
 */

import { useState } from 'react';

import { useTranslation } from '../../i18n/useTranslation.js';
import { errorFields, errorMessageKey } from '../../lib/apiError.js';
import { formatMoney } from '../../lib/currency.js';
import { useAuth } from '../auth/AuthContext.js';
import { useCreateCustomer, useCustomers, useUpdateCustomer, type Customer } from './api.js';

type CustomerType = 'regular' | 'walkin';

const TYPES: CustomerType[] = ['regular', 'walkin'];

/** Digits with at most two decimals, checked as text — never parsed (MONEY-1). */
const MONEY_DRAFT = /^\d{1,12}(\.\d{1,2})?$/;

function TypeChips({ value, onChange }: { value: CustomerType; onChange: (type: CustomerType) => void }): JSX.Element {
  const { t } = useTranslation();

  return (
    <fieldset className="mb-3">
      <legend className="mb-2 text-sm font-medium text-gray-700">{t('customers.type')}</legend>
      <div className="flex flex-wrap gap-2">
        {TYPES.map((type) => (
          <button
            key={type}
            type="button"
            onClick={() => onChange(type)}
            aria-pressed={value === type}
            className={`min-h-touch rounded-full px-4 py-2 text-sm ${
              value === type ? 'bg-green-700 font-medium text-white' : 'bg-white text-gray-700 shadow-sm'
            }`}
          >
            {t(`customers.type.${type}`)}
          </button>
        ))}
      </div>
      {/* What the choice actually does, rather than leaving it to the label. */}
      <p className="mt-2 text-xs text-gray-500">{t(`customers.type.${value}.hint`)}</p>
    </fieldset>
  );
}

function CustomerForm({ existing, onDone }: { existing?: Customer; onDone: () => void }): JSX.Element {
  const { t } = useTranslation();
  const create = useCreateCustomer();
  const update = useUpdateCustomer();

  const [name, setName] = useState(existing?.name ?? '');
  const [type, setType] = useState<CustomerType>(existing?.type ?? 'regular');
  const [phone, setPhone] = useState(existing?.phone ?? '');
  const [address, setAddress] = useState(existing?.address ?? '');
  const [defaultRate, setDefaultRate] = useState(existing?.defaultRate ?? '');
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const pending = create.isPending || update.isPending;

  /*
   * A regular customer's rate is what their monthly bill is built from, so an
   * empty one is a bill that cannot be raised. The server refuses it by name
   * too (AP-1); saying so here means a partner finds out before saving.
   */
  const rateNeeded = type === 'regular' && !MONEY_DRAFT.test(defaultRate);
  const canSubmit = name.trim().length >= 2 && !rateNeeded && !pending;

  async function submit(): Promise<void> {
    setErrorKey(null);
    setFieldErrors({});

    const body = {
      name: name.trim(),
      type,
      ...(phone.trim() === '' ? {} : { phone: phone.trim() }),
      ...(address.trim() === '' ? {} : { address: address.trim() }),
      ...(defaultRate.trim() === '' ? {} : { defaultRate: defaultRate.trim() }),
    };

    try {
      if (existing) {
        await update.mutateAsync({ id: existing.id, changes: body });
      } else {
        await create.mutateAsync(body);
      }
      onDone();
    } catch (error) {
      setErrorKey(errorMessageKey(error));
      setFieldErrors(errorFields(error) ?? {});
    }
  }

  return (
    <form
      className={existing ? 'mt-3 border-t border-gray-100 pt-3' : 'mb-5 rounded-xl bg-white p-4 shadow-sm'}
      onSubmit={(event) => {
        event.preventDefault();
        void submit();
      }}
      noValidate
    >
      {!existing && <h2 className="mb-3 text-base font-semibold text-gray-800">{t('customers.addTitle')}</h2>}

      <label className="mb-1 block text-sm font-medium text-gray-700" htmlFor={`name-${String(existing?.id ?? 'new')}`}>
        {t('customers.name')}
      </label>
      <input
        id={`name-${String(existing?.id ?? 'new')}`}
        value={name}
        onChange={(event) => setName(event.target.value)}
        maxLength={120}
        className="mb-3 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      {fieldErrors['name'] !== undefined && <p className="-mt-2 mb-2 text-sm text-red-700">{fieldErrors['name']}</p>}

      <TypeChips value={type} onChange={setType} />

      {type === 'regular' && (
        <>
          <label
            className="mb-1 block text-sm font-medium text-gray-700"
            htmlFor={`rate-${String(existing?.id ?? 'new')}`}
          >
            {t('customers.rate')}
          </label>
          <input
            id={`rate-${String(existing?.id ?? 'new')}`}
            inputMode="decimal"
            value={defaultRate}
            onChange={(event) => setDefaultRate(event.target.value)}
            placeholder="0.00"
            className="mb-3 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3 tabular-nums"
          />
          {fieldErrors['defaultRate'] !== undefined && (
            <p className="-mt-2 mb-2 text-sm text-red-700">{fieldErrors['defaultRate']}</p>
          )}
        </>
      )}

      <label
        className="mb-1 block text-sm font-medium text-gray-700"
        htmlFor={`phone-${String(existing?.id ?? 'new')}`}
      >
        {t('customers.phone')}
      </label>
      <input
        id={`phone-${String(existing?.id ?? 'new')}`}
        type="tel"
        inputMode="numeric"
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
        placeholder="01XXXXXXXXX"
        className="mb-3 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />
      {fieldErrors['phone'] !== undefined && <p className="-mt-2 mb-2 text-sm text-red-700">{fieldErrors['phone']}</p>}

      <label
        className="mb-1 block text-sm font-medium text-gray-700"
        htmlFor={`address-${String(existing?.id ?? 'new')}`}
      >
        {t('customers.address')}
      </label>
      <input
        id={`address-${String(existing?.id ?? 'new')}`}
        value={address}
        onChange={(event) => setAddress(event.target.value)}
        maxLength={500}
        className="mb-3 min-h-touch w-full rounded-lg border border-gray-300 px-4 py-3"
      />

      {errorKey !== null && (
        <div role="alert" className="mb-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p>{t(errorKey)}</p>
        </div>
      )}

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={!canSubmit}
          className="min-h-[52px] flex-1 rounded-xl bg-green-700 text-base font-medium text-white disabled:opacity-50"
        >
          {t(pending ? 'customers.saving' : 'customers.save')}
        </button>
        {existing && (
          <button
            type="button"
            onClick={onDone}
            className="min-h-touch rounded-xl bg-gray-100 px-4 py-2 text-sm text-gray-700"
          >
            {t('app.cancel')}
          </button>
        )}
      </div>

      {/* Deactivation, never deletion (BR-08): a customer who stops taking milk
          still has months of deliveries and bills behind their name. */}
      {existing && (
        <button
          type="button"
          onClick={() => void update.mutateAsync({ id: existing.id, changes: { isActive: !existing.isActive } })}
          disabled={pending}
          className={`mt-2 min-h-touch w-full rounded-xl px-4 py-2 text-sm disabled:opacity-50 ${
            existing.isActive ? 'bg-red-50 text-red-800' : 'bg-green-50 text-green-800'
          }`}
        >
          {t(existing.isActive ? 'customers.deactivate' : 'customers.reactivate')}
        </button>
      )}
    </form>
  );
}

function CustomerRow({ customer, canManage }: { customer: Customer; canManage: boolean }): JSX.Element {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);

  return (
    <li className={`rounded-xl bg-white p-4 shadow-sm ${customer.isActive ? '' : 'opacity-70'}`}>
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="truncate font-medium text-gray-800">{customer.name}</p>
          <p className="text-xs text-gray-500">
            {customer.phone ?? t('customers.noPhone')}
            {customer.defaultRate !== null && ` · ${formatMoney(customer.defaultRate)}/L`}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {!customer.isActive && (
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
              {t('customers.inactive')}
            </span>
          )}
          <span className="rounded-full bg-green-50 px-2 py-0.5 text-xs text-green-800">
            {t(`customers.type.${customer.type}`)}
          </span>
        </div>
      </div>

      {canManage && !isEditing && (
        <button
          type="button"
          onClick={() => setIsEditing(true)}
          className="mt-2 min-h-touch text-sm font-medium text-green-800 underline"
        >
          {t('customers.edit')}
        </button>
      )}

      {canManage && isEditing && <CustomerForm existing={customer} onDone={() => setIsEditing(false)} />}
    </li>
  );
}

export function CustomersScreen(): JSX.Element {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { data: customers = [], isLoading, isError, refetch } = useCustomers();
  const [isAdding, setIsAdding] = useState(false);

  // Customers are who the farm sells to, which §1.4 makes a partner's job.
  const canManage = user?.role === 'admin' || user?.role === 'partner';

  return (
    <div className="page-container">
      <h1 className="py-4 text-xl font-semibold text-gray-800">{t('customers.title')}</h1>

      {canManage &&
        (isAdding ? (
          <CustomerForm onDone={() => setIsAdding(false)} />
        ) : (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="mb-4 flex min-h-touch w-full items-center justify-between rounded-xl bg-green-700 px-4 py-3 text-white"
          >
            <span className="text-sm font-medium">{t('customers.add')}</span>
            <span aria-hidden="true">+</span>
          </button>
        ))}

      {isLoading && (
        <p role="status" className="py-8 text-center text-gray-500">
          {t('app.loading')}
        </p>
      )}

      {isError && (
        <div role="alert" className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
          <p className="mb-2">{t('error.network')}</p>
          <button type="button" onClick={() => void refetch()} className="min-h-touch font-medium underline">
            {t('app.retry')}
          </button>
        </div>
      )}

      {customers.length === 0 && !isLoading && <p className="py-8 text-center text-gray-500">{t('customers.empty')}</p>}

      <ul className="space-y-3">
        {customers.map((customer) => (
          <CustomerRow key={customer.id} customer={customer} canManage={canManage} />
        ))}
      </ul>
    </div>
  );
}
